import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { Fragment, useEffect, useMemo } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { article as articleData, data, section as sectionData } from "shared/data";
import { theme } from "shared/lib/styles";
import { getRssFeedLink } from "shared/lib/utils";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { LiveNewsletterWidget } from "scmp-app/components/live/live-newsletter-widget";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import {
  useAutoRefreshWhenIdlingForTenMinutes,
  useInfiniteScrollTriggerReference,
} from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { livePageLatestContent$key } from "scmp-app/queries/__generated__/livePageLatestContent.graphql";
import type { livePageQuery } from "scmp-app/queries/__generated__/livePageQuery.graphql";

import { getDesktopAdUnit, getMobileAdUnit, getTabletAdUnit } from "./helpers";
import {
  AdContainer,
  Container,
  Heading,
  Newsletter,
  StyledAdSlot,
  StyledAppFooter,
  StyledContentItemLivePrimary,
  StyledLoading,
} from "./styles";

const query = graphql`
  query livePageQuery(
    $after: String
    $count: Int = 30
    $applicationIds: [String]
    $excludeArticleTypeIds: [String]
    $excludeSectionIds: [String]
    $scmpPlusPaywallTypeIds: [String]
  ) {
    ...livePageLatestContent
      @arguments(
        after: $after
        count: $count
        applicationIds: $applicationIds
        excludeArticleTypeIds: $excludeArticleTypeIds
        excludeSectionIds: $excludeSectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
  }
`;

const livePageSize = 30;
const adZone = "homelive/home";

const LivePage: RelayComponent<unknown, livePageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const {
    data: contentList,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<livePageQuery, livePageLatestContent$key>(
    graphql`
      fragment livePageLatestContent on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
        excludeArticleTypeIds: { type: "[String]", defaultValue: [] }
        excludeSectionIds: { type: "[String]", defaultValue: [] }
        scmpPlusPaywallTypeIds: { type: "[String]" }
      )
      @refetchable(queryName: "livePageLatestPaginationQuery") {
        contents(
          after: $after
          first: $count
          contentTypes: [ARTICLE, GALLERY, VIDEO]
          articleFilterInput: { sponsorTypes: [NONE] }
          articleExcludeInput: {
            articleTypeIds: $excludeArticleTypeIds
            paywallTypeIds: $scmpPlusPaywallTypeIds
          }
          filter: { applicationIds: $applicationIds }
          exclude: { sectionIds: $excludeSectionIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "livePage__article_contents") {
          edges {
            node {
              ...livePrimaryContentItemContent
            }
          }
        }
      }
    `,
    data,
  );

  const rssFeedLink = getRssFeedLink();

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(livePageSize),
  });

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  const isShowFooter = useMemo(
    () => !contentList.contents?.edges || !hasNextPage,
    [contentList.contents?.edges, hasNextPage],
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  useAutoRefreshWhenIdlingForTenMinutes();

  return (
    <Container>
      <PageSeo
        additionalLinkTags={[rssFeedLink]}
        description="Breaking news from Hong Kong, China and Asia including geopolitics, diplomacy, economy, technology, lifestyle, entertainment and sport."
        openGraph={{
          images: [
            {
              url: "https://www.scmp.com/static/img/icons/scmp-meta-1200x630.png",
            },
          ],
        }}
        title="Latest and Breaking News"
        twitter={{
          cardType: "summary",
        }}
      />
      <Heading>Latest</Heading>
      {contentList.contents.edges.map(({ node }, index) => (
        <Fragment key={index}>
          {index === 5 && (
            <Newsletter>
              <LiveNewsletterWidget />
            </Newsletter>
          )}
          {index > 0 && index % 5 === 0 && (
            <AdContainer>
              <StyledAdSlot
                adUnit={getDesktopAdUnit(index / 5 - 1)}
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
              <StyledAdSlot
                adUnit={getTabletAdUnit(index / 5 - 1)}
                breakpoint={theme.breakpoints.only("tablet")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
              <StyledAdSlot
                adUnit={getMobileAdUnit(index / 5 - 1)}
                breakpoint={theme.breakpoints.only("mobile")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
            </AdContainer>
          )}
          <StyledContentItemLivePrimary reference={node} />
        </Fragment>
      ))}
      {hasNextPage && <StyledLoading ref={element => infiniteScrollTriggerReference(element)} />}
      {isShowFooter && <StyledAppFooter />}
    </Container>
  );
};

LivePage.displayName = "LivePage";

const pageProps: PageProps = {
  appBarConfiguration: {
    desktopBreakpoint: "desktop",
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      tabletUp: 4,
    },
  },
};

export default withRelay(LivePage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext() {
    return {
      applicationIds: [null, data.application.scmp.entityUuid],
      excludeArticleTypeIds: [
        articleData.types.series.entityUuid,
        articleData.types.letters.entityUuid,
        articleData.types.recap.entityUuid,
      ],
      excludeSectionIds: [
        sectionData.yp.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.posties.entityUuid,
        sectionData.recap.entityUuid,
      ],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
    };
  },
});
